import React from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { trackDataLayer } from "../../../../helpers/track";
import { eventNameDataLayer } from "../../../../constants/track";
import styles from "./feedTypeChips.module.css";

// FeedQuery::TYPES minus "referral" — referrals stay out of the feed until Community
// Referrals launches (GROWTH-7127 decision 1; ticket AC excludes the chip entirely).
// Exported: useDeepLinkedType (GROWTH-7131) validates a deep-linked ?type= against these same values.
export const TYPES = [
  { value: null, labelKey: "dashboard_feed_filter_all" },
  { value: "profile", labelKey: "dashboard_feed_filter_profiles" },
  { value: "organization", labelKey: "dashboard_feed_filter_organizations" },
  { value: "event", labelKey: "dashboard_feed_filter_events" },
  { value: "job", labelKey: "dashboard_feed_filter_jobs" }
];

/**
 * A row's count from GET /api-v2/feed's `type_counts` ({profile, organization, event, job}); "All" is
 * their sum. undefined when the response carries no type_counts, or no number for that type.
 *
 * @param {Object|null} typeCounts
 * @param {string|null} value
 * @returns {number|undefined}
 */
const countFor = (typeCounts, value) => {
  if (!typeCounts) return undefined;
  if (value !== null) return Number.isFinite(typeCounts[value]) ? typeCounts[value] : undefined;

  const counts = TYPES.filter(type => type.value !== null).map(type => typeCounts[type.value]);
  return counts.every(Number.isFinite) ? counts.reduce((sum, count) => sum + count, 0) : undefined;
};

/**
 * The My Feed type filter: All / Profiles / Organizations / Events / Jobs.
 *
 * `variant="chips"` is the horizontal chip row above a stream (the Saved page, and the Feed page
 * below 1024px). `variant="list"` is the Feed page's sidebar "Show" card (GROWTH-7573), one row per
 * type, each with its count once the API sends `type_counts`.
 *
 * Every click is tracked as `feed_filter_type { type, placement }` — placement "sidebar" for the
 * list, "chips" for the chip row; type "all" for All.
 *
 * @param {Object} props
 * @param {string|null} props.value the active FeedQuery type, or null for "All"
 * @param {Function} props.onChange (type: string|null) => void
 * @param {"chips"|"list"} [props.variant]
 * @param {Object|null} [props.typeCounts] list only: GET /api-v2/feed's type_counts, null = not sent
 * @param {string} [props.className]
 */
const FeedTypeChips = ({ value, onChange, variant = "chips", typeCounts = null, className }) => {
  const { t } = useTranslation();
  const isList = variant === "list";

  const handleClick = type => {
    trackDataLayer(eventNameDataLayer.feedFilterType, { type: type || "all", placement: isList ? "sidebar" : "chips" });
    onChange(type);
  };

  if (!isList) {
    return (
      <div className={cn(styles.chips, className)} role="group" aria-label={t("dashboard_feed_filter_group_label")}>
        {TYPES.map(type => (
          <button
            key={type.value || "all"}
            type="button"
            className={cn(styles.chip, { [styles.chipActive]: value === type.value })}
            aria-pressed={value === type.value}
            onClick={() => handleClick(type.value)}
          >
            {t(type.labelKey)}
          </button>
        ))}
      </div>
    );
  }

  return (
    <section className={cn(styles.show, className)}>
      <h2 className={styles.showTitle}>{t("dashboard_feed_filter_show_title")}</h2>
      <div className={styles.rows} role="group" aria-label={t("dashboard_feed_filter_group_label")}>
        {TYPES.map(type => {
          const active = value === type.value;
          const count = countFor(typeCounts, type.value);

          return (
            <button
              key={type.value || "all"}
              type="button"
              className={cn(styles.row, { [styles.rowActive]: active })}
              aria-pressed={active}
              onClick={() => handleClick(type.value)}
            >
              <span>{t(type.labelKey)}</span>
              {count !== undefined ? <span className={styles.rowCount}>{count}</span> : null}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default FeedTypeChips;
